import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Eye, EyeOff, ShieldCheck, ShieldAlert, Check, X } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import api, { formatApiError } from "@/lib/api";

/**
 * Compute a 0–4 strength score for the given password.
 *   0 = empty, 1 = weak, 2 = fair, 3 = strong, 4 = excellent
 */
function scorePassword(pw) {
  if (!pw) return { score: 0, label: "", tone: "" };
  let s = 0;
  const has = { lower: /[a-z]/.test(pw), upper: /[A-Z]/.test(pw), num: /[0-9]/.test(pw), sym: /[^A-Za-z0-9]/.test(pw) };
  const classes = Object.values(has).filter(Boolean).length;
  if (pw.length >= 8) s += 1;
  if (pw.length >= 12) s += 1;
  if (classes >= 2) s += 1;
  if (classes >= 3 && pw.length >= 10) s += 1;
  if (classes >= 4 && pw.length >= 14) s = 4;
  const meta = [
    { label: "", tone: "" },
    { label: "Weak", tone: "bg-red-500" },
    { label: "Fair", tone: "bg-amber-500" },
    { label: "Strong", tone: "bg-emerald-500" },
    { label: "Excellent", tone: "bg-emerald-600" },
  ];
  return { score: s, ...meta[s], has };
}

const Rule = ({ ok, children }) => (
  <li className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-700" : "text-[var(--muted)]"}`}>
    {ok ? <Check size={14} className="shrink-0" /> : <X size={14} className="shrink-0 opacity-40" />}
    <span>{children}</span>
  </li>
);

export default function ResetPassword() {
  const [params] = useSearchParams();
  // Supabase's password-reset email delivers the recovery token as a URL
  // hash fragment (`#access_token=...&type=recovery`). Fall back to a
  // query-string `?token=...` for backward compatibility.
  const [token, setToken] = useState(params.get("token") || "");
  useEffect(() => {
    if (token) return;
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (!hash) return;
    const q = new URLSearchParams(hash);
    const t = q.get("access_token");
    if (t) {
      setToken(t);
      // Strip the hash so refreshes don't re-use the recovery token
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [token]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const strength = useMemo(() => scorePassword(password), [password]);
  const matches = confirm.length > 0 && confirm === password;
  const canSubmit = strength.score >= 2 && matches && !loading;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) return setErr("Passwords do not match");
    if (strength.score < 2) return setErr("Please choose a stronger password");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setOk(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <SiteLayout branch="main">
        <section className="section max-w-md mx-auto">
          <div className="bg-white border border-[var(--line)] p-10 text-center">
            <div className="w-14 h-14 rounded-full bg-red-50 border border-red-100 mx-auto flex items-center justify-center">
              <ShieldAlert className="text-red-600" size={26} />
            </div>
            <h1 className="font-display text-3xl text-[var(--navy)] mt-6">Reset link is invalid</h1>
            <p className="text-[var(--muted)] mt-3 text-sm leading-relaxed">
              This password-reset link is missing its token, has already been used, or has expired.
              Request a new one and we'll email you a fresh link.
            </p>
            <Link to="/forgot-password" className="btn btn-navy mt-8 inline-flex" data-testid="reset-request-new">
              Request a new link
            </Link>
          </div>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout branch="main">
      <section className="section max-w-md mx-auto">
        <div className="text-center mb-8">
          <span className="eyebrow-dark">RESET YOUR PASSWORD</span>
          <h1 className="font-display text-4xl text-[var(--navy)] mt-3">Choose a new password.</h1>
          <p className="text-[var(--muted)] text-sm mt-3">
            Make it long, mix character types. We'll never store it in plain text.
          </p>
        </div>

        {ok ? (
          <div className="bg-white border border-[var(--line)] p-10 text-center" data-testid="reset-success">
            <div className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-100 mx-auto flex items-center justify-center">
              <CheckCircle2 className="text-emerald-600" size={26} />
            </div>
            <h2 className="font-display text-2xl text-[var(--navy)] mt-6">Password updated.</h2>
            <p className="text-[var(--muted)] text-sm mt-3">Taking you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-[var(--line)] p-8 space-y-5" data-testid="reset-form">
            {/* New password field with reveal toggle */}
            <div className="field">
              <label className="field-label">New password</label>
              <div className="relative">
                <input
                  required
                  type={showPw ? "text" : "password"}
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password"
                  className="pr-10"
                  autoComplete="new-password"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--navy)]"
                  data-testid="reset-toggle-visibility"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Strength meter — 4-segment bar */}
            {password && (
              <div data-testid="reset-strength">
                <div className="flex gap-1.5 h-1.5">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className={`flex-1 rounded-full transition-colors ${
                        i <= strength.score ? strength.tone : "bg-[var(--line)]"
                      }`}
                    />
                  ))}
                </div>
                <p className="text-xs mt-2 flex items-center gap-1.5 text-[var(--muted)]">
                  <ShieldCheck size={13} />
                  <span>
                    Strength:{" "}
                    <span className="font-medium text-[var(--navy)]" data-testid="reset-strength-label">
                      {strength.label || "—"}
                    </span>
                  </span>
                </p>
                <ul className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3">
                  <Rule ok={password.length >= 8}>At least 8 characters</Rule>
                  <Rule ok={password.length >= 12}>12+ for strong</Rule>
                  <Rule ok={strength.has?.upper && strength.has?.lower}>Upper &amp; lower case</Rule>
                  <Rule ok={strength.has?.num}>A number</Rule>
                  <Rule ok={strength.has?.sym}>A symbol (! @ # …)</Rule>
                </ul>
              </div>
            )}

            {/* Confirm password */}
            <div className="field">
              <label className="field-label">Confirm password</label>
              <input
                required
                type={showPw ? "text" : "password"}
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm"
                autoComplete="new-password"
                aria-invalid={confirm && !matches}
              />
              {confirm && !matches && (
                <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                  <X size={12} /> Passwords don't match yet
                </p>
              )}
              {matches && (
                <p className="text-xs text-emerald-700 mt-1.5 flex items-center gap-1">
                  <Check size={12} /> Passwords match
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="btn btn-navy w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="reset-submit"
            >
              {loading ? "Updating…" : "Update password"}
            </button>

            {err && (
              <p className="text-red-700 text-xs bg-red-50 border border-red-100 rounded p-2.5" data-testid="reset-error">
                {err}
              </p>
            )}

            <p className="text-xs text-[var(--muted)] text-center pt-2">
              Remembered it? <Link to="/login" className="text-[var(--navy)] underline">Back to sign in</Link>
            </p>
          </form>
        )}
      </section>
    </SiteLayout>
  );
}
